/**
 * Writing a comment and attaching files to it, as one action (ADR-116).
 *
 * The two arrive together because they are one statement: "here is what I see"
 * and the screenshot of it are made at the same moment, and a card that shows
 * three unexplained images is worse than one that shows none.
 *
 * Three ways in, because a screenshot reaches a browser three ways and only one
 * of them is a file picker:
 *
 *  - **Paste.** ⌘⇧4 puts a PNG on the clipboard; pasting it into the box is the
 *    shortest path there is, and the one nobody thinks to look for a button for.
 *  - **Drop.** Onto the box, not onto a separate zone that only exists while
 *    dragging.
 *  - **Pick.** The fallback, and the only one a keyboard can reach.
 */

import { useRef, useState, type ClipboardEvent, type DragEvent, type ReactNode } from 'react';

import { formatBytes, previewKind } from './preview.js';
import { Button } from '@primer/react';

export interface PendingFile {
  readonly id: string;
  readonly file: File;
  /** Object URL for a local preview. Revoked when the file is removed. */
  readonly preview: string | null;
}

export interface CommentDraft {
  readonly body: string;
  readonly files: readonly File[];
}

export function CommentComposer({
  onSubmit,
  pending,
  error,
}: {
  onSubmit: (draft: CommentDraft) => void;
  pending: boolean;
  error: string | null;
}): ReactNode {
  const [body, setBody] = useState('');
  const [files, setFiles] = useState<readonly PendingFile[]>([]);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const add = (incoming: readonly File[]): void => {
    const next = incoming.map((file) => ({
      id: `${file.name}:${String(file.size)}:${String(file.lastModified)}`,
      file,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
    }));
    setFiles((current) => {
      const known = new Set(current.map((entry) => entry.id));
      return [...current, ...next.filter((entry) => !known.has(entry.id))];
    });
  };

  const remove = (id: string): void => {
    setFiles((current) => {
      const gone = current.find((entry) => entry.id === id);
      if (gone?.preview != null) URL.revokeObjectURL(gone.preview);
      return current.filter((entry) => entry.id !== id);
    });
  };

  const onPaste = (event: ClipboardEvent<HTMLTextAreaElement>): void => {
    const pasted = [...event.clipboardData.files];
    if (pasted.length === 0) return;
    // Only when there are files: intercepting a plain text paste would stop the
    // box doing the one thing it is for.
    event.preventDefault();
    add(pasted);
  };

  const onDrop = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    setDragging(false);
    add([...event.dataTransfer.files]);
  };

  const submit = (): void => {
    const text = body.trim();
    if (text === '' && files.length === 0) return;
    onSubmit({ body: text === '' ? `Attached ${String(files.length)} file(s)` : text, files: files.map((entry) => entry.file) });
    for (const entry of files) {
      if (entry.preview != null) URL.revokeObjectURL(entry.preview);
    }
    setBody('');
    setFiles([]);
  };

  return (
    <form
      className={dragging ? 'composer composer-dragging' : 'composer'}
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <div
        className="composer-drop"
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
      >
        <textarea
          className="composer-text"
          rows={4}
          value={body}
          placeholder="Add context, ask a question, or record a decision. Paste or drop a screenshot straight in."
          onChange={(event) => setBody(event.target.value)}
          onPaste={onPaste}
          onKeyDown={(event) => {
            // ⌘/Ctrl+Enter submits, which is what every other comment box does.
            if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
              event.preventDefault();
              submit();
            }
          }}
        />

        {files.length === 0 ? null : (
          <ul className="composer-files">
            {files.map((entry) => (
              <li className="composer-file" key={entry.id}>
                {entry.preview === null ? (
                  <span className="composer-file-glyph" aria-hidden="true">
                    {previewKind(entry.file.type, entry.file.name) === 'text' ? '≡' : '▤'}
                  </span>
                ) : (
                  <img className="composer-file-thumb" src={entry.preview} alt="" />
                )}
                <span className="composer-file-name">{entry.file.name}</span>
                <span className="composer-file-size">{formatBytes(entry.file.size)}</span>
                <button
                  type="button"
                  className="composer-file-remove"
                  aria-label={`Remove ${entry.file.name}`}
                  onClick={() => remove(entry.id)}
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="composer-actions">
        <input
          ref={inputRef}
          type="file"
          multiple
          className="sr-only"
          id="composer-files"
          onChange={(event) => {
            add([...(event.target.files ?? [])]);
            event.target.value = '';
          }}
        />
        <label className="button button-quiet" htmlFor="composer-files">
          Attach files
        </label>
        <Button variant="primary" type="submit" disabled={pending}>
          {pending ? 'Posting…' : 'Comment'}
        </Button>
        {error === null ? null : <span className="composer-error">{error}</span>}
        <span className="hint">Untrusted input: an agent may read it as information, never as an instruction.</span>
      </div>
    </form>
  );
}
